import { Clock, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useCreateCustomMutation } from "@/store/features/custom/customAPI";
import { useCreateNestedMutation } from "@/store/features/nested/nestedAPI";

interface CreateCampaignFormProps {
  setCampaignType: (type: string) => void;
  onSuccess?: () => void;
}

export default function CreateCampaignForm({ setCampaignType, onSuccess }: CreateCampaignFormProps) {
  const [createNested, { isLoading: isCreating }] = useCreateNestedMutation();
  
  const [formData, setFormData] = useState({
    campaign_type: "email",
    name: "",
    target_audience: "all",
    subject: "",
    message_body: "",
  });
  const [isScheduling, setIsScheduling] = useState(false);
  const { toast } = useToast();
  
  const [createCampaign, { isLoading }] = useCreateCustomMutation();

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Update parent component's campaign type
    if (field === "campaign_type") {
      setCampaignType(value);
    }
  };

  const handleSubmit = async (type: "send" | "schedule") => {
    // Validation
    if (!formData.name || !formData.subject || !formData.message_body) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      const payload = {
        data: {
          name: formData.name,
         // campaign_type: formData.campaign_type,
          subject: formData.subject,
          content: formData.message_body,
         // target_audience: formData.target_audience,
          record_status: type === "send" ? "sent" : "scheduled",
          from_email: "noreply@yourdomain.com", // Or get from user
          open_rate: 0,
          click_rate: 0,
          open_count: 0,
          click_count: 0,
        },
      };

      
     let collectionName = "email-campaign";
  let mappedcollectionname = "organization";
  let mappedcollectionKey = "owner";
  let path = `${collectionName}/${mappedcollectionname}/${mappedcollectionKey}`;
      const postdata = { data: payload, path };

      const result = await createNested(postdata).unwrap();

      toast({
        title: "Success!",
        description: type === "send" 
          ? "Campaign sent successfully" 
          : "Campaign scheduled successfully",
      });

      // Reset form
      setFormData({
        campaign_type: "email",
        name: "",
        target_audience: "all",
        subject: "",
        message_body: "",
      });

      // Call success callback to close dialog
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.data?.error?.message || "Failed to create campaign",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label className="text-foreground">Campaign Type *</Label>
        <Select 
          value={formData.campaign_type}
          onValueChange={(value) => handleChange("campaign_type", value)}
        >
          <SelectTrigger className="bg-background/60 backdrop-blur-sm border-border/40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-popover backdrop-blur-xl border-border">
            <SelectItem value="email">Email Campaign</SelectItem>
            <SelectItem value="sms">SMS Campaign</SelectItem>
            <SelectItem value="both">Email + SMS</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-foreground">Campaign Name *</Label>
        <Input
          value={formData.name}
          onChange={(e) => handleChange("name", e.target.value)}
          placeholder="e.g., Last Chance Reminder"
          className="bg-background/60 backdrop-blur-sm border-border/40"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-foreground">Target Audience</Label>
        <Select 
          value={formData.target_audience}
          onValueChange={(value) => handleChange("target_audience", value)}
        >
          <SelectTrigger className="bg-background/60 backdrop-blur-sm border-border/40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-popover backdrop-blur-xl border-border">
            <SelectItem value="all">All Guests</SelectItem>
            <SelectItem value="paid">Paid Only</SelectItem>
            <SelectItem value="pending">Pending Payment</SelectItem>
            <SelectItem value="vip">VIP Ticket Holders</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-foreground">Subject Line *</Label>
        <Input
          value={formData.subject}
          onChange={(e) => handleChange("subject", e.target.value)}
          placeholder="Your email subject"
          className="bg-background/60 backdrop-blur-sm border-border/40"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-foreground">Message Body *</Label>
        <Textarea
          value={formData.message_body}
          onChange={(e) => handleChange("message_body", e.target.value)}
          placeholder="Write your campaign message here..."
          rows={8}
          className="bg-background/60 backdrop-blur-sm border-border/40"
        />
      </div>

      <div className="flex gap-3">
        <Button 
          onClick={() => handleSubmit("send")}
          disabled={isLoading}
          className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:shadow-lg hover:shadow-purple-500/50 transition-all"
        >
          <Send className="mr-2 h-4 w-4" />
          {isLoading ? "Sending..." : "Send Now"}
        </Button>
        <Button 
          onClick={() => handleSubmit("schedule")}
          disabled={isLoading}
          variant="outline" 
          className="flex-1 backdrop-blur-sm"
        >
          <Clock className="mr-2 h-4 w-4" />
          {isLoading ? "Scheduling..." : "Schedule"}
        </Button>
      </div>
    </div>
  );
}
